/**
 * TAHLEEL.ai — Analysis Progress & Pipeline Visibility
 *
 * Shows the coach what the AI is doing at each stage:
 *   1. Uploading video
 *   2. Vision Preflight (crop, colors, team names)
 *   3. Cleaning Frames (filter stats)
 *   4. Detecting Players (YOLOv8)
 *   5. Tactical Analysis (Claude AI)
 *   6. Complete
 *
 * Also displays a post-analysis pipeline summary so the coach sees
 * how many frames were cleaned, what crop was applied, etc.
 */

import React from 'react';
import {
  Upload, Eye, Filter, Users, Brain, CheckCircle2,
  Sparkles, Crop, Trash2, Shield
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
//  PIPELINE STAGES (used during live progress)
// ─────────────────────────────────────────────────────────────

const STAGES = [
  {
    id: 'upload',
    label: 'Uploading Video',
    labelAr: 'رفع الفيديو',
    icon: Upload,
    color: 'text-accent-blue',
    bgColor: 'bg-accent-blue/10',
    borderColor: 'border-accent-blue/30',
  },
  {
    id: 'preflight',
    label: 'Vision Preflight',
    labelAr: 'فحص الرؤية',
    description: 'Analyzing broadcast style, detecting jersey colors & team names',
    icon: Eye,
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10',
    borderColor: 'border-purple-400/30',
  },
  {
    id: 'cleaning',
    label: 'Cleaning Frames',
    labelAr: 'تنظيف الإطارات',
    description: 'Removing replays, close-ups, blurry frames & overlays',
    icon: Filter,
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/30',
  },
  {
    id: 'detection',
    label: 'Detecting Players',
    labelAr: 'كشف اللاعبين',
    description: 'Identifying players, filtering off-pitch persons',
    icon: Users,
    color: 'text-green-400',
    bgColor: 'bg-green-400/10',
    borderColor: 'border-green-400/30',
  },
  {
    id: 'analysis',
    label: 'Tactical Analysis',
    labelAr: 'التحليل التكتيكي',
    description: 'AI generating formations, weaknesses, recommendations',
    icon: Brain,
    color: 'text-sky-400',
    bgColor: 'bg-sky-400/10',
    borderColor: 'border-sky-400/30',
  },
  {
    id: 'complete',
    label: 'Complete',
    labelAr: 'مكتمل',
    icon: CheckCircle2,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400/10',
    borderColor: 'border-emerald-400/30',
  },
];

// ─────────────────────────────────────────────────────────────
//  LIVE PROGRESS TRACKER (during analysis)
// ─────────────────────────────────────────────────────────────

export const AnalysisProgressTracker = ({ currentStage = 'upload', elapsedSeconds = 0 }) => {
  const currentIdx = STAGES.findIndex(s => s.id === currentStage);

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Elapsed time */}
      <div className="text-center mb-6">
        <span className="text-text-muted text-sm font-mono">
          {Math.floor(elapsedSeconds / 60)}:{String(Math.floor(elapsedSeconds % 60)).padStart(2, '0')}
        </span>
      </div>

      {/* Stage list */}
      <div className="space-y-3">
        {STAGES.map((stage, idx) => {
          const Icon = stage.icon;
          const isActive = idx === currentIdx;
          const isDone = idx < currentIdx;
          const isPending = idx > currentIdx;

          return (
            <div
              key={stage.id}
              className={`
                flex items-center gap-4 px-4 py-3 rounded-xl border transition-all duration-500
                ${isActive ? `${stage.bgColor} ${stage.borderColor} animate-pulse-slow` : ''}
                ${isDone ? 'bg-emerald-500/5 border-emerald-500/20' : ''}
                ${isPending ? 'bg-navy-900/50 border-surface-border/30 opacity-40' : ''}
              `}
            >
              {/* Icon */}
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                ${isActive ? stage.bgColor : ''}
                ${isDone ? 'bg-emerald-500/10' : ''}
                ${isPending ? 'bg-navy-800' : ''}
              `}>
                {isDone ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Icon className={`w-5 h-5 ${isActive ? stage.color : 'text-text-muted'}`} />
                )}
              </div>

              {/* Label + description */}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${isActive ? 'text-text-primary' : isDone ? 'text-emerald-400' : 'text-text-muted'}`}>
                  {stage.label}
                </div>
                {isActive && stage.description && (
                  <div className="text-xs text-text-muted mt-0.5 animate-fade-in">
                    {stage.description}
                  </div>
                )}
              </div>

              {/* Status dot */}
              {isActive && (
                <div className="relative flex-shrink-0">
                  <div className={`w-2.5 h-2.5 rounded-full ${stage.color.replace('text-', 'bg-')}`} />
                  <div className={`absolute inset-0 w-2.5 h-2.5 rounded-full ${stage.color.replace('text-', 'bg-')} ping-slow`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  PIPELINE SUMMARY (after analysis completes)
// ─────────────────────────────────────────────────────────────

export const PipelineSummary = ({ pipeline }) => {
  if (!pipeline) return null;

  const filter = pipeline.frame_filter || {};
  const crop = pipeline.broadcast_crop || {};
  const preflight = pipeline.preflight || {};

  const totalChecked = filter.total_checked || 0;
  const totalPassed = filter.total_passed || 0;
  const totalRejected = filter.total_rejected || 0;
  const passRate = filter.pass_rate_pct || 0;

  const items = [];

  // Preflight
  if (preflight.ran) {
    const teams = preflight.teams;
    const teamStr = teams?.team_0_name && teams?.team_1_name
      ? `${teams.team_0_name} vs ${teams.team_1_name}`
      : 'Teams identified from broadcast';

    items.push({
      icon: Eye,
      color: 'text-purple-400',
      bg: 'bg-purple-400/10',
      title: 'Vision Preflight',
      detail: teamStr,
    });
  }

  // Broadcast crop
  if (crop.source === 'vision_preflight' || crop.top_pct > 0 || crop.right_pct > 0) {
    const cropParts = [];
    if (crop.top_pct > 0) cropParts.push(`top ${Math.round(crop.top_pct * 100)}%`);
    if (crop.right_pct > 0) cropParts.push(`right ${Math.round(crop.right_pct * 100)}%`);
    if (crop.bottom_pct > 0) cropParts.push(`bottom ${Math.round(crop.bottom_pct * 100)}%`);
    if (crop.left_pct > 0) cropParts.push(`left ${Math.round(crop.left_pct * 100)}%`);

    items.push({
      icon: Crop,
      color: 'text-orange-400',
      bg: 'bg-orange-400/10',
      title: 'Broadcast Crop Applied',
      detail: cropParts.length > 0 ? `Cropped: ${cropParts.join(', ')}` : 'No crop needed',
      badge: crop.source === 'vision_preflight' ? 'AI' : 'Default',
    });
  }

  // Frame filter
  if (totalChecked > 0) {
    items.push({
      icon: Filter,
      color: 'text-amber-400',
      bg: 'bg-amber-400/10',
      title: 'Frame Cleaning',
      detail: `${totalChecked} frames checked — ${totalPassed} passed, ${totalRejected} rejected`,
      stats: {
        passRate: `${passRate}%`,
        greenFails: filter.rejected_green || 0,
        blurFails: filter.rejected_blur || 0,
        wideFails: filter.rejected_wide || 0,
        overlayFails: filter.rejected_overlay || 0,
      },
    });
  }

  // Contrast enhancement
  if (crop.enhance_contrast) {
    items.push({
      icon: Sparkles,
      color: 'text-cyan-400',
      bg: 'bg-cyan-400/10',
      title: 'Contrast Enhanced',
      detail: `Brightness adjusted to ${crop.brightness_adjust || 1.0}x`,
    });
  }

  // Player detection
  if (pipeline.players_detected > 0) {
    items.push({
      icon: Shield,
      color: 'text-green-400',
      bg: 'bg-green-400/10',
      title: 'Player Detection',
      detail: `${pipeline.players_detected} player detections across ${pipeline.frames_to_yolo || 0} frames`,
    });
  }

  if (items.length === 0) return null;

  return (
    <div className="card space-y-1">
      <h3 className="section-header mb-4">
        <Brain className="w-5 h-5 text-accent-blue" />
        AI Pipeline Summary
      </h3>

      <div className="space-y-3">
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={idx} className="flex items-start gap-3 py-2">
              {/* Icon */}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item.bg}`}>
                <Icon className={`w-4 h-4 ${item.color}`} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-text-primary">{item.title}</span>
                  {item.badge && (
                    <span className="badge-info text-[10px] px-1.5 py-0.5">{item.badge}</span>
                  )}
                </div>
                <div className="text-xs text-text-secondary mt-0.5">{item.detail}</div>

                {/* Frame filter breakdown */}
                {item.stats && totalRejected > 0 && (
                  <div className="flex flex-wrap gap-3 mt-2">
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">
                      {item.stats.passRate} pass rate
                    </span>
                    {item.stats.greenFails > 0 && (
                      <span className="text-[10px] font-mono text-red-400 bg-red-400/10 px-2 py-0.5 rounded">
                        {item.stats.greenFails} no pitch
                      </span>
                    )}
                    {item.stats.blurFails > 0 && (
                      <span className="text-[10px] font-mono text-red-400 bg-red-400/10 px-2 py-0.5 rounded">
                        {item.stats.blurFails} blurry
                      </span>
                    )}
                    {item.stats.overlayFails > 0 && (
                      <span className="text-[10px] font-mono text-red-400 bg-red-400/10 px-2 py-0.5 rounded">
                        {item.stats.overlayFails} overlay
                      </span>
                    )}
                    {item.stats.wideFails > 0 && (
                      <span className="text-[10px] font-mono text-red-400 bg-red-400/10 px-2 py-0.5 rounded">
                        {item.stats.wideFails} close-up
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total frames saved callout */}
      {totalRejected > 0 && (
        <div className="mt-4 pt-3 border-t border-surface-border flex items-center gap-2">
          <Trash2 className="w-4 h-4 text-amber-400" />
          <span className="text-xs text-text-secondary">
            <span className="text-amber-400 font-semibold">{totalRejected} garbage frames removed</span>
            {' '}— saving analysis cost and improving accuracy
          </span>
        </div>
      )}
    </div>
  );
};

export default AnalysisProgressTracker;
